import { ShieldCheck, Sparkles, Swords } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { type AppendCursor, appendedItems } from "../combatPerformance";
import { type EquipmentItem, type IncomingHit } from "../types";
import { EquipmentSlotIcon, QUALITY_LABELS } from "./EquipmentIcon";

export function EquipmentDropNotifier({
  drops,
  scopeKey,
}: {
  drops: EquipmentItem[];
  scopeKey: string;
}) {
  const knownIDs = useRef(new Set(drops.map((item) => item.id)));
  const cursor = useRef<AppendCursor>({ count: drops.length, tailID: drops.at(-1)?.id ?? "" });
  const [pending, setPending] = useState<EquipmentItem[]>([]);
  const [active, setActive] = useState<EquipmentItem | null>(null);

  useEffect(() => {
    knownIDs.current = new Set(drops.map((item) => item.id));
    cursor.current = { count: drops.length, tailID: drops.at(-1)?.id ?? "" };
    setPending([]);
    setActive(null);
  }, [scopeKey]);

  useEffect(() => {
    const result = appendedItems(drops, cursor.current, knownIDs.current);
    cursor.current = result.cursor;
    const added = result.added;
    if (added.length > 0) setPending((current) => [...current, ...added]);
  }, [drops, scopeKey]);

  useEffect(() => {
    if (active || pending.length === 0) return;
    setActive(pending[0]);
    setPending((current) => current.slice(1));
  }, [active, pending]);

  useEffect(() => {
    if (!active) return;
    const timer = window.setTimeout(() => setActive(null), 1800);
    return () => window.clearTimeout(timer);
  }, [active]);

  if (!active) return null;
  const special = active.quality === "epic" || active.quality === "legendary";
  return (
    <div
      key={active.id}
      className={`equipment-drop-notice quality-${active.quality}`}
      role="status"
      aria-live="polite"
    >
      {special && (
        <span className="equipment-drop-particles" aria-hidden="true">
          {Array.from({ length: 8 }, (_, index) => (
            <i key={index} />
          ))}
        </span>
      )}
      <span className="equipment-drop-icon">
        <EquipmentSlotIcon
          slot={active.slot}
          tier={active.tier}
          size={38}
          legendaryId={active.legendaryId}
          templateId={active.templateId}
        />
      </span>
      <span className="equipment-drop-copy">
        <small>获得装备</small>
        <strong>{active.name}</strong>
        <em>{QUALITY_LABELS[active.quality]}</em>
      </span>
      {special && <Sparkles className="equipment-drop-spark" size={19} aria-hidden="true" />}
    </div>
  );
}

export function IncomingHitFeedback({
  hits,
  now,
  compact = false,
}: {
  hits?: IncomingHit[];
  now: number;
  compact?: boolean;
}) {
  const activeHits = (hits ?? []).filter((hit) => hit.at > 0 && Math.abs(now - hit.at) < 900);
  if (activeHits.length === 0) return null;
  return (
    <div className={`incoming-hit-layer${compact ? " is-compact" : ""}`} aria-live="polite">
      {activeHits.map((hit, index) => (
        <div className="incoming-hit-event" key={`${hit.at}-${hit.sourceId}-${index}`}>
          <span
            className={`incoming-impact${hit.blocked > 0 ? " is-blocked" : " is-damage"}`}
            aria-hidden="true"
          >
            {hit.blocked > 0 ? (
              <ShieldCheck size={compact ? 13 : 17} />
            ) : (
              <Swords size={compact ? 13 : 17} />
            )}
          </span>
          {hit.blocked > 0 && (
            <strong className="combat-float is-blocked">格挡 -{hit.blocked}</strong>
          )}
          {hit.damage > 0 && (
            <strong className="combat-float is-damage">
              {hit.sourceId === "burn" ? "灼烧 " : "攻击 "}-{hit.damage}
            </strong>
          )}
        </div>
      ))}
    </div>
  );
}
